import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import SearchBar from "formElements/inputs/SearchBar.component";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  root: {
    padding: 10,
    gap: 10,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 10,
    justifyContent: "space-between",
    backgroundColor: theme.color.background,
  },
  labelText: {
    fontSize: 18,
    color: theme.color.backgroundText,
  },
  labelTextBold: {
    fontWeight: theme.font.weight.semibold,
  },
  userListContainer: {
    marginLeft: 16,
    marginTop: 4,
    marginBottom: 8,
  },
  userRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  userText: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
  },
});

export default function TeamSelectionModal(props) {
  const {
    open,
    onClose,
    selectedTeamIds,
    setSelectedTeamIds,
    recipients,
    setRecipients,
  } = props;

  const { organizationColor, teamList, personsByTeam } =
    useContext(OrganizationContext);

  const [searchText, setSearchText] = useState("");
  const [localTeamIds, setLocalTeamIds] = useState(new Set());
  const [localUserIds, setLocalUserIds] = useState(new Set());

  useEffect(() => {
    setLocalTeamIds(new Set(selectedTeamIds));
    setLocalUserIds(new Set(recipients));

    // Calculate which teams should be marked as selected based on recipients
    // This handles update mode where recipients exist but teams need to be calculated
    if (recipients && Array.isArray(recipients) && recipients.length > 0) {
      const recipientSet = new Set(recipients);
      const fullySelectedTeamIds = new Set();

      // Check each team to see if all its members are in recipients
      Object.entries(personsByTeam).forEach(([teamId, persons]) => {
        if (persons && persons.length > 0) {
          const allMembersSelected = persons.every((person) =>
            recipientSet.has(person.personId),
          );
          if (allMembersSelected) {
            fullySelectedTeamIds.add(teamId);
          }
        }
      });

      // Merge with explicitly selected teams
      const combinedTeamIds = new Set([
        ...selectedTeamIds,
        ...fullySelectedTeamIds,
      ]);

      setLocalTeamIds(combinedTeamIds);
    }
  }, [selectedTeamIds, recipients, personsByTeam]);

  const toggleTeam = useCallback(
    (teamId) => {
      const updatedTeams = new Set(localTeamIds);
      const updatedUsers = new Set(localUserIds);
      const teamUserIds = (personsByTeam[teamId] || []).map(
        (person) => person.personId,
      );

      if (updatedTeams.has(teamId)) {
        updatedTeams.delete(teamId);
        teamUserIds.forEach((userId) => {
          updatedUsers.delete(userId);
        });
      } else {
        updatedTeams.add(teamId);
        teamUserIds.forEach((userId) => {
          updatedUsers.add(userId);
        });
      }

      setLocalTeamIds(updatedTeams);
      setLocalUserIds(updatedUsers);
    },
    [localTeamIds, localUserIds, personsByTeam],
  );

  const toggleUser = useCallback(
    (teamId, userId) => {
      const updatedTeams = new Set(localTeamIds);
      const updatedUsers = new Set(localUserIds);

      if (updatedUsers.has(userId)) {
        updatedUsers.delete(userId);
      } else {
        updatedUsers.add(userId);
      }

      const teamUserIds = (personsByTeam[teamId] || []).map(
        (person) => person.personId,
      );
      const isTeamFullySelected = teamUserIds.every((teamUserId) => {
        return updatedUsers.has(teamUserId);
      });

      if (isTeamFullySelected) {
        updatedTeams.add(teamId);
      } else {
        updatedTeams.delete(teamId);
      }

      setLocalTeamIds(updatedTeams);
      setLocalUserIds(updatedUsers);
    },
    [localTeamIds, localUserIds, personsByTeam],
  );

  const areAllTeamsSelected = useMemo(() => {
    const eligibleTeams = teamList.filter((team) => {
      return !team.locationId;
    });

    return eligibleTeams.every((team) => {
      return localTeamIds.has(team.teamId);
    });
  }, [teamList, localTeamIds]);

  const toggleSelectAll = useCallback(() => {
    const eligibleTeams = teamList.filter((team) => {
      return !team.locationId;
    });

    if (areAllTeamsSelected) {
      setLocalTeamIds(new Set());
      setLocalUserIds(new Set());
      return;
    }

    const newTeamIds = new Set(eligibleTeams.map((team) => team.teamId));
    const newUserIds = new Set(
      eligibleTeams.flatMap((team) => {
        return (personsByTeam[team.teamId] || []).map(
          (person) => person.personId,
        );
      }),
    );

    setLocalTeamIds(newTeamIds);
    setLocalUserIds(newUserIds);
  }, [areAllTeamsSelected, teamList, personsByTeam]);

  const onSave = useCallback(() => {
    setSelectedTeamIds([...localTeamIds]);
    setRecipients([...localUserIds]);
    onClose([...localTeamIds]);
  }, [localTeamIds, localUserIds, onClose, setSelectedTeamIds, setRecipients]);

  const filterTeam = useCallback(
    (team) => {
      const normalizedSearch = searchText.toLowerCase();

      if (team.name.toLowerCase().includes(normalizedSearch)) {
        return true;
      }

      const members = personsByTeam[team.teamId] || [];
      const hasMatchingMember = members.some((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return fullName.includes(normalizedSearch);
      });

      return hasMatchingMember;
    },
    [searchText, personsByTeam],
  );

  return (
    <CustomModal open={open} onClose={onSave} title="Select Teams" scroll>
      <View style={classes.root}>
        <SearchBar value={searchText} onChangeText={setSearchText} />

        <PressableContainer style={classes.row} onPress={toggleSelectAll}>
          <Text style={[classes.labelText, classes.labelTextBold]}>
            All Teams
          </Text>
          <Icon
            name={areAllTeamsSelected ? "check" : "checkbox-blank-outline"}
            size={28}
            color={organizationColor}
          />
        </PressableContainer>

        <View>
          {teamList
            .filter((team) => {
              return !team.locationId && filterTeam(team);
            })
            .map((team) => {
              const users = (personsByTeam[team.teamId] || []).sort((a, b) =>
                a.lastName.localeCompare(b.lastName),
              );
              const isTeamSelected = localTeamIds.has(team.teamId);
              const isDisabled = users.length === 0;
              return (
                <View key={team.teamId}>
                  <PressableContainer
                    style={classes.row}
                    onPress={() => {
                      toggleTeam(team.teamId);
                    }}
                    disabled={isDisabled}
                  >
                    <Text style={classes.labelText}>{team.name}</Text>

                    {!isDisabled && (
                      <Icon
                        name={
                          isTeamSelected ? "check" : "checkbox-blank-outline"
                        }
                        size={28}
                        color={organizationColor}
                      />
                    )}
                  </PressableContainer>

                  {users.length > 0 ? (
                    <View style={classes.userListContainer}>
                      {users.map((user) => {
                        const isUserSelected = localUserIds.has(user.personId);

                        return (
                          <PressableContainer
                            key={user.personId}
                            style={classes.userRow}
                            onPress={() => {
                              toggleUser(team.teamId, user.personId);
                            }}
                          >
                            <Text style={classes.userText}>
                              {user.firstName} {user.lastName}
                            </Text>
                            <Icon
                              name={
                                isUserSelected
                                  ? "check"
                                  : "checkbox-blank-outline"
                              }
                              size={22}
                              color={organizationColor}
                            />
                          </PressableContainer>
                        );
                      })}
                    </View>
                  ) : (
                    <Text style={[classes.userListContainer, classes.userText]}>
                      No associated individuals
                    </Text>
                  )}
                </View>
              );
            })}
        </View>
      </View>
    </CustomModal>
  );
}
